import { enumOverrideType } from "../constants";
import { QChange } from "./QChange";
import { QObjDBClass } from "./QObjDBClass";
import { Globals } from "./Globals";

// Override sort priorities (lower = applied first).
// Positions must exist before holders can reference them.
// Adds must exist before other operations can target them.
const OBJ_TYPE_PRIORITY = { Position: 0, Holder: 1 };
const OVERRIDE_TYPE_PRIORITY = {
  [enumOverrideType.add]: 0,
  [enumOverrideType.change]: 1,
  [enumOverrideType.move]: 1,
  [enumOverrideType.moveout]: 1,
  [enumOverrideType.delete]: 1,
  [enumOverrideType.lock]: 1,
  [enumOverrideType.comment]: 1
};
const DEFAULT_OBJ_TYPE_PRIORITY = OBJ_TYPE_PRIORITY.Holder;
const DEFAULT_OVERRIDE_TYPE_PRIORITY = 1;

export class QOverrideMgr extends QObjDBClass {
  constructor() {
    super();
  }

  GetSortedOverrides() {
    return [...this.GetObjectList()].sort((a, b) => {
      const objTypeA = a.SmartGetFieldValue("objType") ?? "Holder";
      const objTypeB = b.SmartGetFieldValue("objType") ?? "Holder";

      const objTypeDiff =
        (OBJ_TYPE_PRIORITY[objTypeA] ?? DEFAULT_OBJ_TYPE_PRIORITY) -
        (OBJ_TYPE_PRIORITY[objTypeB] ?? DEFAULT_OBJ_TYPE_PRIORITY);
      if (objTypeDiff !== 0) return objTypeDiff;

      const typeA = a.SmartGetFieldValue("Type");
      const typeB = b.SmartGetFieldValue("Type");

      return (
        (OVERRIDE_TYPE_PRIORITY[typeA] ?? DEFAULT_OVERRIDE_TYPE_PRIORITY) -
        (OVERRIDE_TYPE_PRIORITY[typeB] ?? DEFAULT_OVERRIDE_TYPE_PRIORITY)
      );
    });
  }

  ApplyOverrides() {
    var adds = [];
    var overridesToApply = this.GetSortedOverrides();

    for (var i in overridesToApply) {
      var newOverride = overridesToApply[i];
      var newAdd = newOverride.ApplyChanges();
      if (newAdd) adds.push(newAdd);
    }
    return adds;
  }

  RemoveDataOverride(dbObj, override) {
    var overrideClass = override.GetParentClass();
    overrideClass.JournalObjList();

    override.Journal(false);
    let targetDBObj = dbObj ?? override.fChanges[0].fDBObj;
    // revert change
    override.fChanges[0].fIsReverted = true;
    // saw this being used for deleting boxes...?
    override.SetPersistent(false);
    // apply to dbobj
    override.ApplyChanges(targetDBObj);
    // remove override from list ??
    this.GetObjMgr().DeleteObject(override);
  }

  CopyOverrides(priorDoc) {
    var exOverrideClass = priorDoc.GetOverrideClass();
    //exOverrideClass.ResetIndex();
    var exOverrides = exOverrideClass.GetObjectList();
    var overrideObjMgr = this.GetObjMgr();
    overrideObjMgr.PurgeData();
    //this.ResetIndex();
    for (var i in exOverrides) {
      var exOverride = exOverrides[i];
      var newOverride = exOverride.Clone(true, this);
      this.InsertObject(newOverride);
    }
  }

  SetBoxOverride(dbObj, incomingType, changeDict) {
    var override = this.GetExistingBoxOverride(dbObj);
    if (override == null) {
      var boxObjID = dbObj.GetObjectRecordID();
      override = this.DoCreateDBOverride(
        incomingType,
        boxObjID,
        changeDict,
        null,
        dbObj?.IsPositionObj?.()
      );
      override.Journal(true);
      this._markFilterRefreshPending();
      return;
    }
    //this.JournalObjList();
    override.Journal(false);
    // Decide what the new type will be
    var currentType = override.SmartGetFieldValue("Type");
    if (currentType == enumOverrideType.add && incomingType == enumOverrideType.delete) {
      //This is effectively a revert. We need to clean up the created node and the references to the object.
      this.RemoveDataOverride(dbObj, override);
      const scm = this.fWSMGr?.GetSubChartMgr();
      scm?.FindSCSubNodesFromDataObj(dbObj).forEach(subNode => {
        scm.RemoveSCNode(dbObj.GetObjectID(), subNode.GetLayout().GetSubChartID());
      });
      dbObj.GetParent()?.RemoveChild(dbObj);
      // OCN5-6486: snapshot before delete so undo can restore the box into its parent's children.
      dbObj.GetParentClass()?.JournalObjList?.();
      dbObj.GetParentClass()?.DeleteObject(dbObj);
      // Return: override is gone, so the processing below would act on a deleted object.
      return this._markFilterRefreshPending();
    }
    var newType;
    if (currentType == enumOverrideType.move && incomingType == enumOverrideType.change)
      newType = enumOverrideType.move;
    else if (
      currentType == enumOverrideType.add &&
      [enumOverrideType.change, enumOverrideType.move].includes(incomingType)
    )
      newType = enumOverrideType.add;
    else newType = incomingType;

    var targetChange = override.fChanges[0];
    if (dbObj) targetChange.fDBObj = dbObj;
    //TODO: Need some consolidation/validation for repeated or redundant changes
    changeDict["author"] = changeDict["author"] || this.fWSMGr?.GetUserName();
    changeDict["date"] = changeDict["date"] || new Date().toUTCString();
    targetChange.UpdateChange(changeDict, newType);
    override.SmartSetFieldValue("Type", newType);
    override.ApplyChanges();
    this._markFilterRefreshPending();
  }

  _markFilterRefreshPending() {
    const wsMgr = Globals._wsMgr;
    if (!wsMgr) return;
    if (!wsMgr.GetDoc()?.IsCustomChart()) return;
    wsMgr.fFilterRefreshPending = true;
  }

  RefreshFiltersIfCustomChart() {
    const wsMgr = Globals._wsMgr;
    if (!wsMgr) return false;
    const doc = wsMgr.GetDoc();
    if (!doc?.IsCustomChart()) return false;
    // ApplyChartFilters calls SwitchToSubChartByObj which requires an active
    // subchart. Skip if none exists yet (e.g. during initial chart load via
    // CopyAndApplyOverrides, before the canvas has switched to any subchart).
    if (!wsMgr.GetSubChartMgr?.()?.GetCurrentSubChart()) return false;
    wsMgr.ApplyChartFilters();
    wsMgr.ApplyChartHighlights();
    return true;
  }

  DoCreateDBOverride(type, boxID, changeDict = {}, displayObj = null, isPosition = false) {
    var overrideObj = this.AddNewObject();
    overrideObj.SmartSetFieldValue("Type", type);
    overrideObj.SmartSetFieldValue("RefID", boxID);
    overrideObj.SmartSetFieldValue("override_id", boxID);
    overrideObj.SmartSetFieldValue("objType", isPosition ? "Position" : "Holder");
    if (displayObj) overrideObj.SmartSetFieldValue("displayObject", displayObj);

    var objDB = this.GetObjDB();
    var boxClass = objDB.GetClassByName(isPosition ? "Position" : "Box");
    var dbObj = boxClass.FindObject(boxClass.GetRecordIDFieldDef(), boxID, false);
    if (dbObj) {
      dbObj.fCloneBase = dbObj.Clone(true);
    }

    changeDict["author"] = Globals._wsMgr.GetUserName();
    changeDict["date"] = new Date().toUTCString();

    var change = new QChange();
    change.InitChange(this.fWSMGr, changeDict, overrideObj, dbObj, this);
    change.UpdateChange(changeDict, type);
    overrideObj.fChanges = [change];
    overrideObj.ApplyChanges();
    return overrideObj;
  }

  GetExistingBoxOverride(dbObj) {
    return this.GetExistingBoxOverrideByRecordId(
      dbObj.GetObjectRecordID(),
      dbObj.InferObjectType()
    );
  }

  GetExistingBoxOverrideByRecordId(recordId, objType) {
    const IDFieldDef = this.GetFieldDefByName("RefID");
    const overrideObjFieldDef = this.GetFieldDefByID("objType");

    const overrideObjList = this.fObjMgr.FindMatchingObjectsByPredicate(override => {
      const overrideObjType = override.GetFieldValue(overrideObjFieldDef) ?? "Holder";

      if (overrideObjType !== objType) {
        return false;
      }

      const overrideRefID = override.GetFieldValue(IDFieldDef);
      return overrideRefID === recordId;
    });

    for (const anOverrideObj of overrideObjList) {
      if (
        anOverrideObj !== null &&
        recordId != null &&
        recordId == anOverrideObj.SmartGetFieldValue("RefID")
      ) {
        return anOverrideObj;
      }
    }

    return null;
  }

  GetAssociatedObjects(dbObjList, levels) {
    var modifiedObjList = [];
    //Process the inbound DB Object List. Recall adds against the root box, moves into the root box, moves out of the root box, and deletes for any of  them
    var rootObj = dbObjList[0];

    for (var o in dbObjList) {
      var theObj = dbObjList[o];
      var existingOverride = this.GetExistingBoxOverride(theObj);
      if (existingOverride) {
        var overrideType = existingOverride.SmartGetFieldValue("Type");
        if (overrideType == enumOverrideType.delete) continue;
        if (overrideType == enumOverrideType.move) {
          //TODO: Insert logic here to determine if the move should stay or go-
          continue;
        }
        //TODO: Should we actually apply it later/elsehwere for better abstraction?
        //The only thing left here is change, because adds won't apply.
        existingOverride.ApplyChanges();
      }
      modifiedObjList.push(theObj);
    }

    var overridesToApply = this.GetObjectList();
    for (var i in overridesToApply) {
      var theOverride = overridesToApply[i];
      var overrideType = theOverride.SmartGetFieldValue("Type");
      if (overrideType == enumOverrideType.add) {
        var newAdd = theOverride.ApplyChanges();
        if (newAdd && newAdd.GetObjectSupervisorID() == rootObj.GetObjectRecordID())
          modifiedObjList.push(newAdd);
      }
    }

    return modifiedObjList;
  }
}
